import { TestIds } from '@/testIds'
import CategoryField from '@components/CategoryField'
import { usePlaceSearchContext } from '@hooks'
import { Input } from '@components/Field'
import React from 'react'
import { EVENT_TITLE_MAX, EventCreateDraft, chooseCategory, setPlace, setTitle } from './core/draft'
import { CATEGORY_GUESSED_MARKER, TITLE_HELPER, TITLE_PLACEHOLDER } from './core/presentation'
import { Group, GroupBody, GroupHint, GroupMarker, GroupTitle, StepForm } from './EventCreateFlow.styles'
import PlaceField from './PlaceField'

interface WhatStepProps {
	tripId: string
	draft: EventCreateDraft
	onChange: (draft: EventCreateDraft) => void
}

/**
 * Step 1 — What: an optional place, the title (filled from the place until
 * the user types their own) and the category (guessed from the place until the
 * user picks one). Next needs a place or a title. The place search opens on
 * the trip's places and the Suggestions for the destination owning the day.
 */
const WhatStep: React.FC<WhatStepProps> = ({ tripId, draft, onChange }) => {
	const placeSearch = usePlaceSearchContext(tripId, { kind: 'event', dayIndex: draft.tripDayIndex })

	return (
		<StepForm>
			<Group>
				<GroupTitle htmlFor={draft.place ? undefined : 'event-create-place'}>
					Place
					<GroupMarker>(optional)</GroupMarker>
				</GroupTitle>
				<GroupBody>
					<PlaceField
						id="event-create-place"
						place={draft.place}
						onPick={place => onChange(setPlace(draft, place))}
						onClear={() => onChange(setPlace(draft, null))}
						context={placeSearch}
					/>
				</GroupBody>
			</Group>

			<Group>
				<GroupTitle htmlFor="event-create-title">Title</GroupTitle>
				<GroupHint>{TITLE_HELPER}</GroupHint>
				<GroupBody>
					<Input
						id="event-create-title"
						aria-label="Title"
						data-testid={TestIds.itinerary.eventNameField}
						type="text"
						value={draft.title}
						onChange={e => onChange(setTitle(draft, e.target.value))}
						placeholder={TITLE_PLACEHOLDER}
						maxLength={EVENT_TITLE_MAX}
						autoComplete="off"
						data-1p-ignore
					/>
				</GroupBody>
			</Group>

			<Group>
				<GroupTitle htmlFor="event-create-category">
					Category
					{draft.categorySource === 'guessed' && (
						<GroupMarker>({CATEGORY_GUESSED_MARKER})</GroupMarker>
					)}
				</GroupTitle>
				<GroupBody>
					<CategoryField
						id="event-create-category"
						aria-label="Category"
						value={draft.category}
						onChange={category => onChange(chooseCategory(draft, category))}
					/>
				</GroupBody>
			</Group>
		</StepForm>
	)
}

export default WhatStep
